import { Link } from "react-router-dom";

export default function ContentSections() {
  return (
    <>
      {/* Section: What Is Sugargoo Spreadsheet */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
            What Is Sugargoo Spreadsheet?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                A <strong>Sugargoo Spreadsheet</strong> is a carefully organized digital catalog that compiles product links, style recommendations, pricing information, and shopping resources — all designed to help Sugargoo users discover products more efficiently. Rather than scrolling through endless listings, a well-structured spreadsheet gives you instant access to curated finds across multiple categories.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Think of it as your personal shopping assistant in spreadsheet form. Each entry typically includes the product name, direct Sugargoo link, estimated price range, size availability, and community ratings — everything you need to make informed purchasing decisions without the hassle of manual searching.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                The spreadsheet format makes it incredibly easy to filter, sort, and compare products side by side. Whether you are hunting for the latest sneaker releases, building a seasonal wardrobe, or exploring trending streetwear, a Sugargoo Spreadsheet puts organized product data right at your fingertips.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {[
                { title: "Product Discovery", desc: "Browse hundreds of hand-picked items organized by category, price, and popularity — no more endless scrolling through unorganized listings." },
                { title: "Organized Categories", desc: "Every product is sorted into logical categories like Shoes, Hoodies, Jackets, Accessories, and more for effortless navigation." },
                { title: "Community Recommendations", desc: "Products featured in the spreadsheet come from real community feedback, hauls, and reviews — so you know what is trending." },
                { title: "Time Saving", desc: "Skip the research phase and go straight to discovering products. A spreadsheet condenses hours of browsing into minutes." },
                { title: "Better Decisions", desc: "Compare prices, check size charts, and read quick notes before adding items to your Sugargoo cart." },
              ].map((item) => (
                <div key={item.title} className="flex gap-3 p-3 rounded-lg bg-background-50 border border-background-200/50">
                  <div className="w-5 h-5 mt-0.5 flex-shrink-0 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-check-line text-primary-600 text-xs"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800 mb-0.5">{item.title}</h4>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section: Why Use Sugargoo Spreadsheet */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
            Why Use Sugargoo Spreadsheet?
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
            Shopping on Sugargoo can feel overwhelming — thousands of products, multiple sellers, varying quality levels, and complex shipping options. A Sugargoo Spreadsheet eliminates the guesswork and transforms your shopping experience from chaotic to streamlined.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-background-300">
                  <th className="text-left py-3 px-4 font-semibold text-foreground-800">Feature</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground-800">Normal Search</th>
                  <th className="text-left py-3 px-4 font-semibold text-primary-700 bg-primary-50 rounded-t-lg">Sugargoo Spreadsheet</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: "Finding Products", normal: "Slow — manual browsing required", spreadsheet: "Fast — curated lists ready to view" },
                  { feature: "Product Organization", normal: "Poor — scattered across pages", spreadsheet: "Structured — logical category grouping" },
                  { feature: "Beginner Friendly", normal: "No — steep learning curve", spreadsheet: "Yes — guided navigation and tips" },
                  { feature: "Updated Products", normal: "Limited — stale listings", spreadsheet: "Regular updates with fresh finds" },
                  { feature: "Price Comparison", normal: "Manual — open multiple tabs", spreadsheet: "Side-by-side in one view" },
                  { feature: "Community Input", normal: "Hard to find reliable reviews", spreadsheet: "Real feedback from active shoppers" },
                ].map((row) => (
                  <tr key={row.feature} className="border-b border-background-200/50">
                    <td className="py-3 px-4 font-medium text-foreground-800">{row.feature}</td>
                    <td className="py-3 px-4 text-foreground-500">{row.normal}</td>
                    <td className="py-3 px-4 text-primary-700 bg-primary-50/50 font-medium">{row.spreadsheet}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Section: Sugargoo Review 2026 */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
            Sugargoo Review 2026 — Is It Worth Using?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="p-5 rounded-xl bg-accent-50 border border-accent-200">
              <h3 className="font-heading text-lg font-semibold text-accent-800 mb-3 flex items-center gap-2">
                <i className="ri-thumb-up-line text-accent-500"></i> Pros
              </h3>
              <ul className="space-y-2">
                {[
                  "Extensive product catalog spanning fashion, electronics, and lifestyle",
                  "Competitive agent fees compared to other shopping platforms",
                  "Reliable quality inspection photos before international shipping",
                  "Multiple shipping methods including EMS, DHL, and economical sea freight",
                  "User-friendly interface with detailed order tracking",
                  "Active community sharing hauls, reviews, and spreadsheet finds",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-5 rounded-xl bg-secondary-50 border border-secondary-200">
              <h3 className="font-heading text-lg font-semibold text-secondary-800 mb-3 flex items-center gap-2">
                <i className="ri-information-line text-secondary-500"></i> Considerations
              </h3>
              <ul className="space-y-2">
                {[
                  "Shipping costs can add up — always check estimates before ordering",
                  "Delivery times vary significantly between shipping methods",
                  "Product quality may differ from listing photos — use QC inspection",
                  "Some sellers have minimum order quantities for certain items",
                  "Currency conversion and payment processing fees may apply",
                  "Returns and refunds require navigating seller-specific policies",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-information-line text-secondary-500 mt-0.5 flex-shrink-0"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="p-5 rounded-xl bg-background-50 border border-background-200/60">
            <h3 className="font-heading text-lg font-semibold text-foreground-800 mb-3">Our Verdict</h3>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              Sugargoo remains one of the most reliable shopping agents available in 2026. Its combination of reasonable fees, extensive product access, quality inspection services, and a supportive community makes it an excellent choice for both newcomers and experienced shoppers. When paired with a well-organized <strong>Sugargoo Spreadsheet</strong>, the shopping experience becomes significantly faster and more enjoyable. We recommend starting with a small test order to familiarize yourself with the platform before placing larger hauls.
            </p>
          </div>
        </div>
      </section>

      {/* Section: Shipping Cost Explained */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
            Sugargoo Shipping Cost Explained
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
            Understanding shipping costs is crucial for maximizing your Sugargoo shopping experience. Different shipping methods offer different trade-offs between speed and cost. Here is a breakdown of the most popular options available in 2026.
          </p>

          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-background-300">
                  <th className="text-left py-3 px-4 font-semibold text-foreground-800">Method</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground-800">Delivery Time</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground-800">Cost Level</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground-800">Best For</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { method: "EMS", time: "7-15 business days", cost: "Medium", best: "Balanced speed and affordability" },
                  { method: "DHL Express", time: "3-7 business days", cost: "High", best: "Urgent deliveries, premium items" },
                  { method: "Air Shipping (Standard)", time: "10-20 business days", cost: "Low-Medium", best: "Regular hauls, moderate weight" },
                  { method: "Sea Shipping", time: "25-45 business days", cost: "Lowest", best: "Heavy or bulky items, budget hauls" },
                ].map((row) => (
                  <tr key={row.method} className="border-b border-background-200/50">
                    <td className="py-3 px-4 font-medium text-foreground-800">{row.method}</td>
                    <td className="py-3 px-4 text-foreground-600">{row.time}</td>
                    <td className="py-3 px-4 text-foreground-600">{row.cost}</td>
                    <td className="py-3 px-4 text-foreground-600">{row.best}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: "ri-lightbulb-line", title: "Tip: Consolidate Orders", desc: "Combine multiple items into one shipment to reduce per-item shipping costs significantly." },
              { icon: "ri-scales-line", title: "Tip: Weigh Before Shipping", desc: "Use Sugargoo's warehouse weighing service to get accurate shipping estimates before committing." },
            ].map((tip) => (
              <div key={tip.title} className="flex gap-3 p-4 rounded-xl bg-accent-50 border border-accent-200">
                <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${tip.icon} text-accent-600`}></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-800 mb-1">{tip.title}</h4>
                  <p className="text-xs text-foreground-500 leading-relaxed">{tip.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section: Coupon Guide */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
            Sugargoo Coupon &amp; Discount Code Guide
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
            Saving money on Sugargoo is easier than you might think. The platform regularly offers promotional coupons, shipping discounts, and seasonal deals that can significantly reduce your total cost. Here is everything you need to know about maximizing savings on your Sugargoo hauls.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {[
              { icon: "ri-coupon-line", title: "Welcome Coupons", desc: "New users often receive discount vouchers upon registration. Check your account after signing up." },
              { icon: "ri-calendar-event-line", title: "Seasonal Sales", desc: "Major shopping events like 11.11, Black Friday, and holiday seasons bring the deepest discounts." },
              { icon: "ri-group-line", title: "Community Sharing", desc: "Active Sugargoo communities frequently share working coupon codes and limited-time promotional links." },
            ].map((item) => (
              <div key={item.title} className="p-4 rounded-xl bg-background-50 border border-background-200/60 text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className={`${item.icon} text-primary-600`}></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-2">{item.title}</h4>
                <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="p-5 rounded-xl bg-accent-50 border border-accent-200">
            <h3 className="font-heading text-lg font-semibold text-accent-800 mb-3 flex items-center gap-2">
              <i className="ri-star-line text-accent-500"></i> Pro Saving Tips
            </h3>
            <ul className="space-y-2">
              {[
                "Always check the coupon center before placing any order — unclaimed discounts are lost savings.",
                "Combine shipping coupons with consolidated parcels for maximum cost reduction on larger hauls.",
                "Follow Sugargoo on social media for flash sales and exclusive promo codes not available elsewhere.",
                "Use a Sugargoo Spreadsheet to track prices over time and identify genuine discounts versus inflated markdowns.",
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="text-accent-500 font-bold flex-shrink-0">{i + 1}.</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Section: Is Sugargoo Legit */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
            Is Sugargoo Legit? A Complete Safety Analysis
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
            One of the most common questions from new users is whether Sugargoo is a legitimate and safe platform. The short answer is <strong>yes — Sugargoo is a legitimate shopping agent</strong> that has served thousands of international customers. However, as with any online shopping platform, understanding the safety measures and exercising reasonable caution is essential for a positive experience.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: "ri-shield-check-line", title: "Platform Security", desc: "Sugargoo uses encrypted payment processing and secure account systems to protect your personal and financial data during transactions." },
              { icon: "ri-bank-card-line", title: "Payment Safety", desc: "Multiple payment methods are supported including PayPal, which offers buyer protection for added peace of mind on every purchase." },
              { icon: "ri-user-voice-line", title: "Customer Experience", desc: "Thousands of verified users worldwide have shared positive experiences with Sugargoo's service quality and reliability." },
              { icon: "ri-lock-line", title: "Privacy Protection", desc: "Your personal information and shopping data are handled according to strict privacy policies — never shared with third parties without consent." },
              { icon: "ri-error-warning-line", title: "Risk Management", desc: "Always verify individual product listings and seller ratings before purchasing. Use quality inspection photos to confirm items match descriptions." },
              { icon: "ri-global-line", title: "Global Reputation", desc: "Sugargoo has built a solid international reputation over years of operation, with active communities across Reddit, Discord, and shopping forums." },
            ].map((item) => (
              <div key={item.title} className="p-4 rounded-xl bg-background-100 border border-background-200/60">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-3">
                  <i className={`${item.icon} text-primary-600`}></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-2">{item.title}</h4>
                <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}